import { useState, useEffect } from 'react';
import type { AvatarColor } from '@/types';

interface ColorPalette {
  highlight: string;
  base: string;
  shadow: string;
}

const COLOR_MAP: Record<AvatarColor, ColorPalette> = {
  amber:   { highlight: '#FDE68A', base: '#F59E0B', shadow: '#78350F' },
  rose:    { highlight: '#FECDD3', base: '#F43F5E', shadow: '#881337' },
  violet:  { highlight: '#DDD6FE', base: '#8B5CF6', shadow: '#3B0764' },
  sky:     { highlight: '#BAE6FD', base: '#0EA5E9', shadow: '#0C4A6E' },
  emerald: { highlight: '#A7F3D0', base: '#10B981', shadow: '#064E3B' },
  orange:  { highlight: '#FED7AA', base: '#F97316', shadow: '#7C2D12' },
  teal:    { highlight: '#99F6E4', base: '#14B8A6', shadow: '#134E4A' },
  fuchsia: { highlight: '#F5D0FE', base: '#D946EF', shadow: '#4A044E' },
  lime:    { highlight: '#D9F99D', base: '#84CC16', shadow: '#1A2E05' },
  indigo:  { highlight: '#C7D2FE', base: '#6366F1', shadow: '#1E1B4B' },
};

const SIZE_PX: Record<string, number> = {
  xs: 32,
  sm: 64,
  md: 96,
  xl: 128,
  lg: 160,
};

const EMOJI_REM: Record<string, string> = {
  xs: '1rem',
  sm: '1.5rem',
  md: '2.2rem',
  xl: '3rem',
  lg: '4rem',
};

interface Props {
  emoji: string;
  color: AvatarColor;
  avatarImageUrl?: string;
  size?: 'xs' | 'sm' | 'md' | 'xl' | 'lg';
  className?: string;
  onImageError?: () => void;
}

export function CharacterPortrait({ emoji, color, avatarImageUrl, size = 'md', className, onImageError }: Props) {
  const { highlight, base, shadow } = COLOR_MAP[color];
  const px = SIZE_PX[size];
  const [imgError, setImgError] = useState(false);

  // Reset error state when a new URL is provided (e.g. after regeneration)
  useEffect(() => { setImgError(false); }, [avatarImageUrl]);

  if (avatarImageUrl && !imgError) {
    return (
      <div
        className={className}
        style={{
          width: px,
          height: px,
          borderRadius: '50%',
          flexShrink: 0,
          overflow: 'hidden',
          boxShadow: `0 6px 24px ${base}55`,
        }}
        aria-hidden
      >
        <img
          src={avatarImageUrl}
          alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => { setImgError(true); onImageError?.(); }}
        />
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        width: px,
        height: px,
        borderRadius: '50%',
        flexShrink: 0,
        background: `radial-gradient(circle at 32% 28%, ${highlight}, ${base} 52%, ${shadow})`,
        boxShadow: [
          `inset 3px 4px 8px ${highlight}99`,
          `inset -2px -3px 8px ${shadow}66`,
          `0 6px 24px ${base}55`,
        ].join(', '),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: EMOJI_REM[size],
        userSelect: 'none',
        lineHeight: 1,
      }}
      aria-hidden
    >
      {emoji}
    </div>
  );
}
